import { Box, Button, Stack, Typography } from '@mui/material';
import { Spinner } from '@procureai/uix';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useRuntime } from '../RuntimeProvider';
import { I18nNamespace } from '~/common/types';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { useRuntimeAwareAuth } from '~/hooks/useRuntimeAwareAuth/useRuntimeAwareAuth';

export const RequireAuth = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation(I18nNamespace.common, { keyPrefix: 'Login' });
  const { isLoading, isAuthenticated, signinRedirect } = useRuntimeAwareAuth();
  const { runtime } = useRuntime();
  const location = useLocation();
  const redirectInitiated = useRef(false);

  // When using popup as the auth method, isLoading flips between true and false, causing endless popups to be opened
  // `redirectInitiated` ensures there is only one redirect at one given time
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !redirectInitiated.current) {
      redirectInitiated.current = true;
      signinRedirect({ state: { returnUrl: location.pathname } });
    }
  }, [signinRedirect, isAuthenticated, isLoading, location.pathname]);

  // Reset redirect flag when authentication state changes
  useEffect(() => {
    if (isAuthenticated) {
      redirectInitiated.current = false;
    }
  }, [isAuthenticated]);

  if (isLoading && runtime?.type !== 'browserExtension') {
    return <CircularProgressCentered />;
  }

  if (!isAuthenticated && runtime?.type === 'browserExtension') {
    return (
      <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
        <Stack direction="column" spacing={2} minWidth="360px">
          <Typography variant="h4" color="textPrimary">
            {t('extensionWelcome')}
          </Typography>
          <Typography variant="body1" color="textPrimary">
            {t('popupInstruction')}
          </Typography>

          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => {
              signinRedirect();
            }}
          >
            <Spinner className="mr-2" /> {t('button')}
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children;
};
